import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Input,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { FaPlus } from "react-icons/fa"
import { FiSearch } from "react-icons/fi"

import {
  OrganizationsService,
  type QuestionPublic,
  QuestionsService,
  type UserPublic,
} from "@/client"
import type { MembershipPublic } from "@/client/types.gen"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { HtmlContent } from "@/components/Common/HtmlContent"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { PageShell, PageSkeleton } from "@/components/Layout"
import BulkUploadDialog from "@/components/Questions/BulkUploadDialog"
import { QuestionActionsMenu } from "@/components/Questions/QuestionActionsMenu"
import { SubjectSelector } from "@/components/Questions/SubjectSelector"
import { TopicSelector } from "@/components/Questions/TopicSelector"
import { Field } from "@/components/ui/field"
import { InputGroup } from "@/components/ui/input-group"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"

const PER_PAGE = 20

export const Route = createFileRoute("/_layout/questions/")({
  component: QuestionsList,
})

function QuestionsTable() {
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const [selectedSubjects, setSelectedSubjects] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [selectedTopics, setSelectedTopics] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [searchValue, setSearchValue] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [page, setPage] = useState(1)

  const { data: membershipsData } = useQuery<MembershipPublic[]>({
    queryKey: ["myMemberships", currentUser?.id],
    queryFn: () => OrganizationsService.getMyMemberships(),
    enabled: !!currentUser && !currentUser.is_superuser,
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue.trim())
      setPage(1)
    }, 300)

    return () => {
      clearTimeout(handler)
    }
  }, [searchValue])

  const { data, isLoading } = useQuery({
    queryKey: [
      "questions",
      {
        subjectIds: selectedSubjects.map((s) => s.id),
        topicIds: selectedTopics.map((t) => t.id),
        search: debouncedSearch || undefined,
        page,
      },
    ],
    queryFn: () =>
      QuestionsService.listQuestions({
        subjectIds:
          selectedSubjects.length > 0
            ? selectedSubjects.map((s) => s.id)
            : undefined,
        topicIds:
          selectedTopics.length > 0
            ? selectedTopics.map((t) => t.id)
            : undefined,
        search: debouncedSearch || undefined,
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
      }),
  })

  const memberships = membershipsData ?? []

  const canDeleteQuestion = (
    question: Pick<QuestionPublic, "org_id" | "created_by">,
  ) => {
    if (!currentUser) {
      return false
    }

    if (currentUser.is_superuser) {
      return true
    }

    const membership = memberships.find(
      (member: MembershipPublic) => member.org_id === question.org_id,
    )

    if (!membership) {
      return false
    }

    if (membership.role === "admin") {
      return true
    }

    if (
      membership.role === "teacher" &&
      question.created_by === currentUser.id
    ) {
      return true
    }

    return false
  }

  const questions = data?.data ?? []

  let content = <PageSkeleton variant="table" rows={4} />

  if (!isLoading) {
    if (questions.length === 0) {
      content = (
        <AppEmptyState
          title={emptyStateCopy.questions.none.title}
          description={emptyStateCopy.questions.none.description}
          icon={<FiSearch />}
          data-testid="questions-empty-state"
        />
      )
    } else {
      content = (
        <>
          {/* Desktop */}
          <Box display={{ base: "none", md: "block" }}>
            <Table.Root size={{ base: "sm", md: "md" }}>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader w="40%">Question</Table.ColumnHeader>
                  <Table.ColumnHeader w="20%">Subjects</Table.ColumnHeader>
                  <Table.ColumnHeader w="20%">Topics</Table.ColumnHeader>
                  <Table.ColumnHeader w="20%">Actions</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {questions.map((question) => (
                  <Table.Row key={question.id} data-testid="question-card">
                    <Table.Cell w="40%">
                      <HtmlContent
                        lineClamp={2}
                        content={question.question_text}
                      />
                    </Table.Cell>
                    <Table.Cell w="20%">
                      <Flex gap={1} wrap="wrap">
                        {question.subjects.map((subject) => (
                          <Badge key={subject.id} colorPalette="blue" size="sm">
                            {subject.name}
                          </Badge>
                        ))}
                      </Flex>
                    </Table.Cell>
                    <Table.Cell w="20%">
                      <Flex gap={1} wrap="wrap">
                        {question.topics.map((topic) => (
                          <Badge key={topic.id} colorPalette="green" size="sm">
                            {topic.name}
                          </Badge>
                        ))}
                      </Flex>
                    </Table.Cell>
                    <Table.Cell w="20%">
                      <QuestionActionsMenu
                        question={question}
                        canDelete={canDeleteQuestion(question)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>

          {/* Mobile cards */}
          <VStack
            display={{ base: "flex", md: "none" }}
            gap={3}
            align="stretch"
          >
            {questions.map((question) => (
              <Card.Root key={question.id}>
                <Card.Body>
                  <HtmlContent lineClamp={2} content={question.question_text} />
                  {question.subjects.length > 0 && (
                    <Flex gap={1} wrap="wrap" mt={2}>
                      {question.subjects.map((subject) => (
                        <Badge key={subject.id} colorPalette="blue" size="sm">
                          {subject.name}
                        </Badge>
                      ))}
                    </Flex>
                  )}
                  {question.topics.length > 0 && (
                    <Flex gap={1} wrap="wrap" mt={1}>
                      {question.topics.map((topic) => (
                        <Badge key={topic.id} colorPalette="green" size="sm">
                          {topic.name}
                        </Badge>
                      ))}
                    </Flex>
                  )}
                  <Flex justify="flex-end" mt={3}>
                    <QuestionActionsMenu
                      question={question}
                      canDelete={canDeleteQuestion(question)}
                    />
                  </Flex>
                </Card.Body>
              </Card.Root>
            ))}
          </VStack>
        </>
      )
    }
  }

  return (
    <VStack gap={4} align="stretch" data-testid="questions-list">
      {/* Filters */}
      <Flex gap={4} wrap="wrap">
        <Field label="Search" flex={1} minW="280px">
          <InputGroup
            w="100%"
            startElement={<FiSearch />}
            startElementProps={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              h: "100%",
            }}
          >
            <Input
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search questions or subjects"
              data-testid="questions-search-input"
            />
          </InputGroup>
        </Field>

        <Field label="Filter by Subjects" flex={1} minW="300px">
          <SubjectSelector
            selectedSubjects={selectedSubjects}
            onChange={(subjects) => {
              setSelectedSubjects(subjects)
              setPage(1)
            }}
          />
        </Field>

        <Field label="Filter by Topics" flex={1} minW="300px">
          <TopicSelector
            selectedTopics={selectedTopics}
            onChange={(topics) => {
              setSelectedTopics(topics)
              setPage(1)
            }}
          />
        </Field>
      </Flex>

      {!isLoading && (data?.count ?? 0) > 0 && (
        <Text fontSize="sm" color="gray.600">
          Showing {data?.data.length ?? 0} of {data?.count ?? 0} questions
        </Text>
      )}

      {content}

      {(data?.count ?? 0) > PER_PAGE && (
        <PaginationRoot
          count={data?.count ?? 0}
          pageSize={PER_PAGE}
          page={page}
          onPageChange={(e) => setPage(e.page)}
        >
          <HStack justify="center" gap={2}>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </HStack>
        </PaginationRoot>
      )}
    </VStack>
  )
}

function QuestionsList() {
  const breadcrumbs = useRouteBreadcrumbs("questions.index")

  const actions = (
    <Flex gap={2} wrap="wrap" align="center">
      <Link to="/questions/add">
        <Button value="add-question">
          <FaPlus fontSize="16px" />
          Add Question
        </Button>
      </Link>
      <BulkUploadDialog />
    </Flex>
  )

  return (
    <PageShell title="Questions" breadcrumbs={breadcrumbs} actions={actions}>
      <QuestionsTable />
    </PageShell>
  )
}
